/**
 * Negotiation Sheet — the "comps for healthcare" output of the free scenario
 * builder. Like pricing a house: you don't negotiate without 3 comparable
 * sales. For every procedure in the scenario this shows the national average
 * sticker price and the cheapest nearby hospitals (30/60/100-mile radius)
 * with real published prices — so you can call a hospital, ask for their
 * negotiated/cash rate, and anchor the conversation at the local floor.
 *
 * Data: per-code static shards (codes/<type>/<code>.json) which carry
 * national pricing + up to 1,000 hospitals with price and coordinates.
 * Distance is client-side haversine. No DB, no payment.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Navigation, Loader2, Printer, Phone, TrendingDown } from "lucide-react";
import { loadStaticData } from "@/lib/offlineData";
import { useUserLocation } from "@/hooks/useUserLocation";
import { haversineDistance } from "@/lib/scenarioCoverageStore";
import { useBrand } from "@/config/brands";
import type { LocalScenarioProcedure } from "@/lib/scenarioStorage";

interface ShardHospital { id: string; n: string; c: string; s: string; p: number; st?: string; la?: number; lo?: number; }
interface CodeShard {
  data: {
    code: string; codeType: string; description?: string; consumerName?: string;
    pricing?: {
      avgGrossCharge?: number; medianGrossCharge?: number;
      bySettings?: Array<{ setting: string; avgCharge?: number; medianCharge?: number; hospitalCount?: number }>;
    };
    hospitals?: ShardHospital[];
  };
}

interface Comp { name: string; city: string; state: string; price: number; dist: number; }
interface LineComps {
  proc: LocalScenarioProcedure;
  nationalAvg: number | null;
  nationalMedian: number | null;
  comps: Comp[];          // cheapest nearby, price-asc
  areaCount: number;      // hospitals with a price within radius
  loaded: boolean;
}

const TYPE_DIR: Record<string, string> = {
  CPT: "cpt", HCPCS: "hcpcs", DRG: "drg", "MS-DRG": "drg", REV: "rev",
  NDC: "ndc", DRUG: "ndc",
};

const fmtUSD = (n: number) => `$${Math.round(n).toLocaleString()}`;

export function NegotiationSheet({ procedures }: { procedures: LocalScenarioProcedure[] }) {
  const { t } = useTranslation("scenarios");
  const brand = useBrand();
  const { location, isLocating, detectLocation, lookupZip } = useUserLocation(true);
  const [zipInput, setZipInput] = useState("");
  const [radius, setRadius] = useState(60);
  const [lines, setLines] = useState<LineComps[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch each procedure's code shard once; comps recompute on location/radius.
  const [shards, setShards] = useState<Map<string, CodeShard["data"] | null>>(new Map());
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      const next = new Map(shards);
      await Promise.all(procedures.map(async (p) => {
        const dir = TYPE_DIR[p.codeType?.toUpperCase() || ""] || "cpt";
        const key = `${dir}/${p.code}`;
        if (next.has(key)) return;
        const shard = await loadStaticData<CodeShard>(`codes/${dir}/${encodeURIComponent(p.code)}.json`);
        next.set(key, shard?.data || null);
      }));
      if (!cancelled) { setShards(next); setLoading(false); }
    };
    if (procedures.length) run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedures.map(p => `${p.codeType}:${p.code}`).join("|")]);

  useEffect(() => {
    const out: LineComps[] = procedures.map((p) => {
      const dir = TYPE_DIR[p.codeType?.toUpperCase() || ""] || "cpt";
      const shard = shards.get(`${dir}/${p.code}`);
      if (!shard) return { proc: p, nationalAvg: null, nationalMedian: null, comps: [], areaCount: 0, loaded: shards.has(`${dir}/${p.code}`) };
      // Prefer the setting the user chose when the shard breaks it out
      const bySetting = p.setting && p.setting !== "unspecified"
        ? shard.pricing?.bySettings?.find(s => s.setting === p.setting)
        : undefined;
      const nationalAvg = bySetting?.avgCharge ?? shard.pricing?.avgGrossCharge ?? null;
      const nationalMedian = bySetting?.medianCharge ?? shard.pricing?.medianGrossCharge ?? null;
      let comps: Comp[] = [];
      let areaCount = 0;
      if (location && shard.hospitals?.length) {
        const within = shard.hospitals
          .filter(h => h.la != null && h.lo != null && h.p > 0)
          .map(h => ({ h, dist: haversineDistance(location.lat, location.lng, h.la!, h.lo!) }))
          .filter(x => x.dist <= radius)
          .sort((a, b) => a.h.p - b.h.p);
        areaCount = within.length;
        comps = within.slice(0, 3).map(x => ({ name: x.h.n, city: x.h.c, state: x.h.s, price: x.h.p, dist: x.dist }));
      }
      return { proc: p, nationalAvg, nationalMedian, comps, areaCount, loaded: true };
    });
    setLines(out);
  }, [procedures, shards, location, radius]);

  const totals = useMemo(() => {
    let national = 0, floor = 0, floorLines = 0;
    for (const l of lines) {
      const qty = l.proc.quantity || 1;
      if (l.nationalAvg) national += l.nationalAvg * qty;
      if (l.comps[0]) { floor += l.comps[0].price * qty; floorLines++; }
      else if (l.nationalAvg) floor += l.nationalAvg * qty;
    }
    return { national, floor, floorLines };
  }, [lines]);

  const handlePrint = () => window.print();

  return (
    <div id="negotiation-sheet" className="rounded-xl border border-border overflow-hidden">
      {/* Print isolation: when printing, show ONLY the sheet. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #negotiation-sheet, #negotiation-sheet * { visibility: visible !important; }
          #negotiation-sheet { position: absolute; left: 0; top: 0; width: 100%; border: none !important; }
          #negotiation-sheet .no-print { display: none !important; }
        }
      `}</style>

      {/* Header */}
      <div className="px-4 py-3 bg-[#002868] text-white">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[2px] text-[#C5A55A] font-bold">
              {t("negotiation.kicker", { defaultValue: "Price Negotiation Sheet" })}
            </div>
            <div className="text-sm font-semibold mt-0.5">
              {t("negotiation.title", { defaultValue: "Your comps — know the floor before you call" })}
            </div>
          </div>
          <button onClick={handlePrint}
            className="no-print inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold"
            style={{ background: "#C5A55A", color: "#002868", border: "none", cursor: "pointer" }}>
            <Printer className="w-3.5 h-3.5" /> {t("negotiation.print", { defaultValue: "Print / Save PDF" })}
          </button>
        </div>
        <p className="text-[11px] text-white/75 mt-1.5 leading-snug max-w-xl">
          {t("negotiation.explainer", { defaultValue: "Like buying a house: you don't negotiate without comps. For each procedure below — the national average sticker price, and the cheapest published prices near you. Ask each hospital for their cash or negotiated rate, and anchor at your local floor." })}
        </p>
      </div>

      {/* Location + radius */}
      <div className="no-print flex items-center gap-2 px-4 py-2.5 border-b border-border flex-wrap bg-muted/30">
        <MapPin className="w-4 h-4 shrink-0 text-[#B22234]" />
        {location ? (
          <span className="text-sm font-medium">{location.label}</span>
        ) : (
          <span className="text-sm text-muted-foreground">{t("negotiation.setLocation", { defaultValue: "Set your location to pull local comps" })}</span>
        )}
        <div className="flex items-center gap-1.5 ml-auto">
          <input value={zipInput} inputMode="numeric" placeholder="ZIP"
            onChange={e => setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
            onKeyDown={e => { if (e.key === "Enter" && zipInput.length === 5) lookupZip(zipInput); }}
            className="w-[64px] rounded-md border border-border bg-background px-2 py-1 text-sm" />
          <button onClick={() => (zipInput.length === 5 ? lookupZip(zipInput) : detectLocation())} disabled={isLocating}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs">
            {isLocating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Navigation className="w-3.5 h-3.5" />}
          </button>
          <select value={radius} onChange={e => setRadius(Number(e.target.value))}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs">
            {[30, 60, 100].map(r => <option key={r} value={r}>{r} mi</option>)}
          </select>
        </div>
      </div>

      {/* Lines */}
      {loading && lines.every(l => !l.loaded) ? (
        <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> {t("negotiation.loading", { defaultValue: "Pulling national + local prices…" })}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {lines.map((l, i) => (
            <div key={i} className="px-4 py-3">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <span className="font-mono text-xs text-[#B22234] font-bold">{l.proc.codeType} {l.proc.code}</span>
                  <span className="text-sm font-medium ml-2">{l.proc.description}</span>
                  {(l.proc.quantity || 1) > 1 && <span className="text-xs text-muted-foreground ml-1.5">× {l.proc.quantity}</span>}
                </div>
                <div className="text-right shrink-0">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("negotiation.natlAvg", { defaultValue: "natl avg sticker" })} </span>
                  <span className="font-mono text-sm font-bold">{l.nationalAvg ? fmtUSD(l.nationalAvg) : "—"}</span>
                  {l.nationalMedian ? <span className="text-[10px] text-muted-foreground ml-1.5">med {fmtUSD(l.nationalMedian)}</span> : null}
                </div>
              </div>
              {/* Comps */}
              {location ? (
                l.comps.length > 0 ? (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                    {l.comps.map((c, ci) => (
                      <div key={ci} className="rounded-lg border border-border px-2.5 py-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-sm font-bold" style={{ color: ci === 0 ? "#2b5e49" : undefined }}>{fmtUSD(c.price)}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{c.dist.toFixed(0)} mi</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate" title={c.name}>{c.name}</div>
                        <div className="text-[10px] text-muted-foreground/70">{c.city}, {c.state}</div>
                      </div>
                    ))}
                  </div>
                ) : l.loaded ? (
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    {t("negotiation.noLocal", { defaultValue: "No published local price within" })} {radius} mi{l.areaCount === 0 && l.nationalAvg ? ` — ${t("negotiation.useNatl", { defaultValue: "use the national average as your anchor" })}` : ""}
                  </p>
                ) : (
                  <div className="mt-1.5 h-4 w-40 rounded bg-muted animate-pulse" />
                )
              ) : null}
            </div>
          ))}
        </div>
      )}

      {/* Totals + script */}
      <div className="px-4 py-3 border-t-2 border-[#C5A55A] bg-muted/30">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <TrendingDown className="w-4 h-4 text-[#2b5e49]" />
            <span className="font-semibold">{t("negotiation.floorLabel", { defaultValue: "Your negotiation floor" })}</span>
            <span className="font-mono text-lg font-bold text-[#2b5e49]">{totals.floor > 0 ? fmtUSD(totals.floor) : "—"}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            {t("negotiation.natlTotal", { defaultValue: "national sticker total" })}{" "}
            <span className="font-mono font-bold text-foreground">{totals.national > 0 ? fmtUSD(totals.national) : "—"}</span>
          </div>
        </div>
        <div className="mt-2 rounded-lg border border-border bg-background px-3 py-2 flex items-start gap-2">
          <Phone className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#002868]" />
          <p className="text-[11px] leading-snug text-muted-foreground">
            {t("negotiation.script", { defaultValue: "What to say: “I'm a self-pay patient comparing prices. Your published price for [code] is [their price]. [Hospital B] nearby publishes [comp price]. What is your best cash price?” Hospitals must honor their published chargemaster — and they know you can go 20 minutes down the road." })}
          </p>
        </div>
        <p className="text-[9px] text-muted-foreground/70 mt-2 leading-snug">
          {t("negotiation.disclaimer", { defaultValue: "Gross “sticker” prices from each hospital's own published charge files via" })} {brand.name} · {new Date().toLocaleDateString()} · {t("negotiation.notAdvice", { defaultValue: "Informational only — not a quote, bill, or medical advice." })}
        </p>
      </div>
    </div>
  );
}
